import BaseApiService from './base-api.service';
import ApplicationSerializer from '@/utilities/serializers/application';

export const MAX_FILE_SIZE_MB = 5;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1000000;
export const PRIMARY = 'PRIMARY';
export const SECONDARY = 'SECONDARY';

export default class OnboardingService {
  _client = null;
  jwt = null;

  constructor(jwt, baseUrl) {
    if (!jwt) throw new Error('JWT is required');

    this.jwt = jwt;

    this._client = new BaseApiService({
      baseURL: baseUrl,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
    });
  }

  async fetchApplications() {
    const { data } = await this._client.get('/api/applications');
    return ApplicationSerializer.deserialize(data);
  }

  async getApplication(id) {
    const { data } = await this._client.get(`/api/applications/${id}`);
    return ApplicationSerializer.deserialize(data);
  }

  async createApplication(app) {
    const application = ApplicationSerializer.serialize(app);
    const { data } = await this._client.post('/api/applications', {
      application,
    });
    return ApplicationSerializer.deserialize(data);
  }

  deleteApplication(id) {
    return this._client.delete(`api/applications/${id}`);
  }

  updateApplication(id, app) {
    return this._client.put(`/api/applications/${id}`, {
      application: app,
    });
  }

  submitApplication(id) {
    return this._client.put(`/api/applications/${id}/submit`);
  }

  // Update Test (Try/Catch error)
  uploadImage(id, metadata, file) {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const error = new Error(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      error.name = 'FileSizeError';
      throw error;
    }

    const data = new FormData();
    data.append('metadata', JSON.stringify(metadata));
    data.append('file', file);

    return this._client.put(`/api/applications/${id}/images`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  uploadIdImage(applicationId, file, applicant = PRIMARY) {
    return this.uploadImage(applicationId, { tag: 'ID_DOCUMENT', name: file.name, applicant }, file);
  }

  uploadSignatureImage(applicationId, file, applicant = PRIMARY) {
    return this.uploadImage(applicationId, { tag: 'SIGNATURE_IMAGE', name: file.name, applicant }, file);
  }

  uploadAddressVerificationImage(applicationId, file, applicant = PRIMARY) {
    return this.uploadImage(applicationId, { tag: 'ADDRESS_VERIFICATION', name: file.name, applicant }, file);
  }

  removeImage(id, uuid) {
    return this._client.delete(`/api/applications/${id}/images/${uuid}`);
  }

  validatePayment(promoCode) {
    return this._client.post('/api/payment-plans/validate', {
      promoCode,
    });
  }

  fetchPaymentPlans(platformId, promoCode) {
    return this._client.get('/api/payment-plans/search', {
      platformId,
      promoCode,
    });
  }

  fetchPlatforms() {
    return this._client.get('/api/platforms');
  }

  validateTaxId(taxId) {
    return this._client.post('/api/blocked-customers/validate', {
      taxIdPrimary: taxId,
    });
  }

  createPlaidLinkToken() {
    return this._client.post('/api/plaid/link');
  }
}
